import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Receipt,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  RefreshCw,
  User,
  CreditCard,
  Calendar,
  Eye,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Printer,
  FileText
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface CreditOrder {
  id: string;
  user_id: string | null;
  credits_amount: number;
  price_thb: number;
  bonus_credits: number;
  payment_method: string;
  status: string;
  package_name: string | null;
  created_at: string;
  completed_at: string | null;
  guest_name: string | null;
  guest_email: string | null;
  guest_phone: string | null;
  stripe_payment_intent_id: string | null;
  payment_slip_url: string | null;
  admin_notes: string | null;
  profiles?: {
    display_name: string | null;
    email: string | null;
  };
}

const OrderManagement = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<CreditOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed' | 'failed' | 'free'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showChart, setShowChart] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<CreditOrder | null>(null);
  const [showBuyerDialog, setShowBuyerDialog] = useState(false);
  const [processingOrderId, setProcessingOrderId] = useState<string | null>(null);
  const ITEMS_PER_PAGE = 20;

  // Track printed orders (stored in localStorage)
  const [printedOrders, setPrintedOrders] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('printed_order_ids');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      // Fetch orders first
      const { data: ordersData, error: ordersError } = await (supabase as any)
        .from("credit_orders")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);

      if (ordersError) throw ordersError;

      // Get unique user IDs from orders
      const userIds = [...new Set((ordersData || []).map((o: any) => o.user_id).filter(Boolean))];

      // Fetch profiles for those user IDs
      let profilesMap: Record<string, { display_name: string | null; email: string | null }> = {};
      if (userIds.length > 0) {
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("user_id, display_name, email")
          .in("user_id", userIds);

        if (profilesData) {
          profilesMap = profilesData.reduce((acc: any, p: any) => {
            acc[p.user_id] = { display_name: p.display_name, email: p.email };
            return acc;
          }, {});
        }
      }

      // Merge profiles into orders
      const ordersWithProfiles = (ordersData || []).map((order: any) => ({
        ...order,
        profiles: order.user_id ? profilesMap[order.user_id] : undefined
      }));

      setOrders(ordersWithProfiles as CreditOrder[]);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast({
        title: "Error",
        description: "Failed to load orders",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("th-TH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/30">สำเร็จ</Badge>;
      case "pending":
        return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30">รอชำระ</Badge>;
      case "processing":
        return <Badge className="bg-blue-500/20 text-blue-500 border-blue-500/30">กำลังตรวจสอบ</Badge>;
      case "failed":
        return <Badge className="bg-red-500/20 text-red-500 border-red-500/30">ล้มเหลว</Badge>;
      case "cancelled":
        return <Badge className="bg-gray-500/20 text-gray-500 border-gray-500/30">ยกเลิก</Badge>;
      case "expired":
        return <Badge className="bg-orange-500/20 text-orange-500 border-orange-500/30">หมดอายุ</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case "stripe_promptpay":
        return "PromptPay (Stripe)";
      case "manual":
        return "โอนเงิน";
      default:
        return method;
    }
  };

  const getUserDisplay = (order: CreditOrder) => {
    if (order.profiles?.display_name || order.profiles?.email) {
      return order.profiles.display_name || order.profiles.email;
    }
    if (order.guest_name || order.guest_email) {
      return `${order.guest_name || ''} ${order.guest_email ? `(${order.guest_email})` : ''}`.trim() || 'Guest';
    }
    return 'Unknown';
  };

  const getBuyerEmail = (order: CreditOrder) => {
    return order.profiles?.email || order.guest_email || "-";
  };

  const getBuyerPhone = (order: CreditOrder) => {
    return order.guest_phone || "-";
  };

  const handleViewBuyerDetails = (order: CreditOrder) => {
    setSelectedOrder(order);
    setShowBuyerDialog(true);
  };

  // Print receipt handler
  const handlePrintReceipt = (order: CreditOrder) => {
    // Mark as printed
    const newPrintedOrders = new Set(printedOrders);
    newPrintedOrders.add(order.id);
    setPrintedOrders(newPrintedOrders);
    localStorage.setItem('printed_order_ids', JSON.stringify([...newPrintedOrders]));

    // Generate receipt HTML
    const receiptHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>ใบเสร็จรับเงิน - ${order.id.slice(0, 8)}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Sarabun', 'Segoe UI', sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
          .logo { font-size: 28px; font-weight: bold; color: #0891b2; }
          .subtitle { color: #666; margin-top: 5px; }
          .receipt-title { font-size: 24px; margin: 20px 0; text-align: center; }
          .info-section { margin: 20px 0; }
          .info-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dashed #ddd; }
          .info-label { color: #666; }
          .info-value { font-weight: 500; }
          .amount-section { background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .total-row { display: flex; justify-content: space-between; font-size: 24px; font-weight: bold; }
          .footer { text-align: center; margin-top: 40px; color: #666; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">GenCNX</div>
          <div class="subtitle">AI Generation Platform</div>
        </div>
        
        <h1 class="receipt-title">ใบเสร็จรับเงิน</h1>
        
        <div class="info-section">
          <div class="info-row">
            <span class="info-label">เลขที่ใบเสร็จ:</span>
            <span class="info-value">${order.id.slice(0, 8).toUpperCase()}</span>
          </div>
          <div class="info-row">
            <span class="info-label">วันที่:</span>
            <span class="info-value">${formatDate(order.created_at)}</span>
          </div>
          <div class="info-row">
            <span class="info-label">ผู้ซื้อ:</span>
            <span class="info-value">${getUserDisplay(order)}</span>
          </div>
          <div class="info-row">
            <span class="info-label">อีเมล:</span>
            <span class="info-value">${getBuyerEmail(order)}</span>
          </div>
          <div class="info-row">
            <span class="info-label">ช่องทางชำระเงิน:</span>
            <span class="info-value">${getPaymentMethodLabel(order.payment_method)}</span>
          </div>
        </div>
        
        <div class="info-section">
          <div class="info-row">
            <span class="info-label">รายการ:</span>
            <span class="info-value">${order.package_name || order.credits_amount.toLocaleString() + ' เครดิต'}</span>
          </div>
          <div class="info-row">
            <span class="info-label">จำนวนเครดิต:</span>
            <span class="info-value">${order.credits_amount.toLocaleString()} เครดิต</span>
          </div>
          ${order.bonus_credits > 0 ? `
          <div class="info-row">
            <span class="info-label">โบนัส:</span>
            <span class="info-value" style="color: green;">+${order.bonus_credits.toLocaleString()} เครดิต</span>
          </div>
          ` : ''}
        </div>
        
        <div class="amount-section">
          <div class="total-row">
            <span>ยอดรวมทั้งสิ้น</span>
            <span>฿${order.price_thb.toLocaleString()}</span>
          </div>
        </div>
        
        <div class="footer">
          <p>ขอบคุณที่ใช้บริการ GenCNX</p>
          <p>www.gencnx.com</p>
        </div>
        
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(receiptHtml);
      printWindow.document.close();
    }
  };

  // Print Abbreviated Tax Invoice handler
  const handlePrintTaxInvoice = async (order: CreditOrder) => {
    // Generate tax invoice numbers (sequential per month starting at 001)
    const dateObj = new Date(order.created_at);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    
    // Set start of the month in local/database matching time
    const startOfMonth = new Date(order.created_at);
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);
    const endOfOrder = new Date(order.created_at);

    let seqNum = "001";
    try {
      // Query Supabase for count of all orders on the same month created before or at this order's created_at
      const { count, error } = await supabase
        .from("credit_orders")
        .select("*", { count: 'exact', head: true })
        .gte("created_at", startOfMonth.toISOString())
        .lte("created_at", endOfOrder.toISOString());

      if (error) throw error;
      seqNum = String(count !== null && count > 0 ? count : 1).padStart(3, '0');
    } catch (err) {
      console.error("Error calculating tax invoice sequence:", err);
      // Fallback to local state calculation if DB query fails
      const ordersOnSameMonth = orders
        .filter(o => {
          const d = new Date(o.created_at);
          return d.getFullYear() === year && (d.getMonth() + 1) === Number(month);
        })
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      
      const indexOnMonth = ordersOnSameMonth.findIndex(o => o.id === order.id);
      seqNum = String(indexOnMonth !== -1 ? indexOnMonth + 1 : 1).padStart(3, '0');
    }

    const taxInvoiceNo = `TX-${year}${month}${seqNum}`;

    // Calculations
    const totalPrice = order.price_thb;
    const vatRate = 0.07;
    const vatAmount = totalPrice * vatRate / (1 + vatRate);
    const preTaxAmount = totalPrice - vatAmount;

    const formattedTotal = totalPrice.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const formattedVat = vatAmount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const formattedPreTax = preTaxAmount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const taxInvoiceHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>ใบกำกับภาษีอย่างย่อ - ${taxInvoiceNo}</title>
        <link href="https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700&display=swap" rel="stylesheet">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Sarabun', 'Segoe UI', sans-serif;
            color: #111;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
            font-size: 14px;
            line-height: 1.5;
          }
          .header {
            text-align: center;
            margin-bottom: 25px;
          }
          .title {
            font-size: 20px;
            font-weight: 700;
            margin-bottom: 5px;
          }
          .invoice-numbers {
            font-size: 14px;
            color: #333;
          }
          .info-grid {
            display: grid;
            grid-template-columns: 1.2fr 1fr;
            gap: 40px;
            margin-top: 30px;
            margin-bottom: 30px;
          }
          .info-title {
            font-size: 15px;
            font-weight: 700;
            margin-bottom: 8px;
            border-bottom: 1px solid #111;
            padding-bottom: 4px;
          }
          .info-text {
            font-size: 13.5px;
            margin-bottom: 4px;
          }
          .info-label {
            color: #555;
          }
          .table-container {
            margin-bottom: 30px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th {
            border-top: 2px solid #111;
            border-bottom: 2px solid #111;
            padding: 10px 8px;
            font-weight: 700;
            font-size: 13.5px;
          }
          td {
            padding: 12px 8px;
            font-size: 13.5px;
            border-bottom: 1px solid #eee;
          }
          .summary-grid {
            display: grid;
            grid-template-columns: 1.2fr 1fr;
            gap: 40px;
            margin-top: 10px;
          }
          .notes-box {
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            padding: 12px;
            min-height: 90px;
          }
          .notes-title {
            font-weight: 700;
            font-size: 12.5px;
            color: #495057;
            margin-bottom: 4px;
          }
          .notes-content {
            font-size: 12.5px;
            color: #6c757d;
          }
          .signature-box {
            border: 1px solid #dee2e6;
            border-radius: 6px;
            padding: 12px;
            height: 90px;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            align-items: center;
            background: #fff;
            margin-top: 15px;
          }
          .signature-line {
            font-size: 12.5px;
            color: #495057;
            border-top: 1px dotted #adb5bd;
            width: 80%;
            text-align: center;
            padding-top: 5px;
          }
          .summary-row {
            display: flex;
            justify-content: space-between;
            padding: 3px 0;
            font-size: 13.5px;
          }
          .summary-row.highlight {
            background: #f1f3f5;
            padding: 8px 12px;
            font-weight: 700;
            border-radius: 4px;
            font-size: 14px;
            margin: 6px 0;
          }
          .footer {
            text-align: center;
            margin-top: 60px;
            font-size: 13px;
            font-weight: 500;
          }
          @media print {
            body { padding: 10px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1 class="title">ใบกำกับภาษีอย่างย่อ / ใบเสร็จรับเงิน</h1>
          <div class="invoice-numbers">
            เลขที่ <strong>${taxInvoiceNo}</strong>
          </div>
        </div>

        <div class="info-grid">
          <div>
            <h2 class="info-title">ร้านค้าผู้ให้บริการ</h2>
            <div class="info-text" style="font-weight: 600; font-size: 14px;">บริษัท รูบี้ ซี จำกัด (00000)</div>
            <div class="info-text" style="color: #666; font-size: 13px; margin-bottom: 5px;">Ruby Sea Co,. Ltd.</div>
            <div class="info-text">344/193 ซ5/3 ม.8 ต.แม่เหียะ อ.เมือง จ.เชียงใหม่ 50100</div>
            <div class="info-text"><span class="info-label">เลขประจำตัวผู้เสียภาษีอากร:</span> <strong>0505565000217</strong></div>
          </div>
          <div>
            <h2 class="info-title">รายละเอียดลูกค้า</h2>
            <div class="info-text" style="font-weight: 600; font-size: 14px;">${getUserDisplay(order)}</div>
            <div class="info-text"><span class="info-label">ติดต่อ:</span> ${order.guest_phone || order.profiles?.email || '-'}</div>
            <div class="info-text"><span class="info-label">วันที่:</span> ${formatDate(order.created_at)}</div>
          </div>
        </div>

        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th style="text-align: left; width: 5%;">#</th>
                <th style="text-align: left;">รายการสินค้า</th>
                <th style="text-align: center; width: 12%;">จำนวน</th>
                <th style="text-align: right; width: 22%;">ราคาต่อหน่วย</th>
                <th style="text-align: right; width: 22%;">จำนวนเงิน</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="text-align: left; vertical-align: top;">1.</td>
                <td style="text-align: left; vertical-align: top;">
                  ${order.package_name || order.credits_amount.toLocaleString() + ' เครดิต'}
                </td>
                <td style="text-align: center; vertical-align: top;">1</td>
                <td style="text-align: right; vertical-align: top;">${formattedTotal} บาท</td>
                <td style="text-align: right; vertical-align: top;">${formattedTotal} บาท</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="summary-grid">
          <div>
            <div class="notes-box">
              <div class="notes-title">หมายเหตุ</div>
              <div class="notes-content">
                ${order.admin_notes || 'ชำระเงินเรียบร้อยแล้ว'}
              </div>
            </div>
            <div class="signature-box">
              <div class="signature-line">ผู้มีอำนาจลงนาม</div>
            </div>
          </div>
          <div>
            <div class="summary-row">
              <span>ทั้งหมด</span>
              <span>${formattedTotal} บาท</span>
            </div>
            <div class="summary-row">
              <span>ส่วนลด</span>
              <span>0.00 บาท</span>
            </div>
            <div class="summary-row">
              <span>ค่าจัดส่ง</span>
              <span>0.00 บาท</span>
            </div>
            <div class="summary-row highlight">
              <span>รวมราคาสุทธิ (ราคารวมภาษีมูลค่าเพิ่ม)</span>
              <span>${formattedTotal} บาท</span>
            </div>
            <div class="summary-row">
              <span class="info-label">ภาษีมูลค่าเพิ่ม 7%</span>
              <span>${formattedVat} บาท</span>
            </div>
            <div class="summary-row">
              <span class="info-label">ราคาไม่รวมภาษีมูลค่าเพิ่ม</span>
              <span>${formattedPreTax} บาท</span>
            </div>
          </div>
        </div>

        <div class="footer">
          ขอบพระคุณที่ใช้บริการ (Thank you)
        </div>

        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(taxInvoiceHtml);
      printWindow.document.close();
    }
  };

  // Print Batch Abbreviated Tax Invoices handler
  const handlePrintBatchTaxInvoices = async () => {
    if (filteredOrders.length === 0) {
      toast({
        title: "ไม่มีรายการ",
        description: "ไม่มีรายการในตัวกรองปัจจุบันเพื่อสั่งพิมพ์",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "กำลังจัดเตรียมเอกสาร...",
      description: `กำลังประมวลผลใบกำกับภาษีทั้งหมด ${filteredOrders.length} รายการ`,
    });

    // Find all distinct months represented in filteredOrders
    const months = [...new Set(filteredOrders.map(o => {
      const d = new Date(o.created_at);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }))];

    // Build map of orderId -> sequence string for all months represented
    const seqMap: Record<string, string> = {};
    try {
      await Promise.all(months.map(async (m) => {
        const [y, mon] = m.split('-');
        const startOfMonth = new Date(Number(y), Number(mon) - 1, 1);
        const endOfMonth = new Date(Number(y), Number(mon), 1);
        
        const { data, error } = await supabase
          .from("credit_orders")
          .select("id, created_at")
          .gte("created_at", startOfMonth.toISOString())
          .lt("created_at", endOfMonth.toISOString())
          .order("created_at", { ascending: true });
          
        if (error) throw error;
        if (data) {
          data.forEach((o: any, index: number) => {
            seqMap[o.id] = String(index + 1).padStart(3, '0');
          });
        }
      }));
    } catch (err) {
      console.error("Error fetching batch sequences:", err);
      // Fallback: fill with default sequence numbers
      filteredOrders.forEach((o, index) => {
        seqMap[o.id] = String(index + 1).padStart(3, '0');
      });
    }

    const htmlPages: string[] = [];

    for (let index = 0; index < filteredOrders.length; index++) {
      const order = filteredOrders[index];
      const dateObj = new Date(order.created_at);
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');

      const seqNum = seqMap[order.id] || "001";
      const taxInvoiceNo = `TX-${year}${month}${seqNum}`;
      
      const totalPrice = order.price_thb;
      const vatRate = 0.07;
      const vatAmount = totalPrice * vatRate / (1 + vatRate);
      const preTaxAmount = totalPrice - vatAmount;

      const formattedTotal = totalPrice.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const formattedVat = vatAmount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const formattedPreTax = preTaxAmount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

      const pageHtml = `
        <div class="invoice-page">
          <div class="header">
            <h1 class="title">ใบกำกับภาษีอย่างย่อ / ใบเสร็จรับเงิน</h1>
            <div class="invoice-numbers">
              เลขที่ <strong>${taxInvoiceNo}</strong>
            </div>
          </div>

          <div class="info-grid">
            <div>
              <h2 class="info-title">ร้านค้าผู้ให้บริการ</h2>
              <div class="info-text" style="font-weight: 600; font-size: 14px;">บริษัท รูบี้ ซี จำกัด (00000)</div>
              <div class="info-text" style="color: #666; font-size: 13px; margin-bottom: 5px;">Ruby Sea Co,. Ltd.</div>
              <div class="info-text">344/193 ซ5/3 ม.8 ต.แม่เหียะ อ.เมือง จ.เชียงใหม่ 50100</div>
              <div class="info-text"><span class="info-label">เลขประจำตัวผู้เสียภาษีอากร:</span> <strong>0505565000217</strong></div>
            </div>
            <div>
              <h2 class="info-title">รายละเอียดลูกค้า</h2>
              <div class="info-text" style="font-weight: 600; font-size: 14px;">${getUserDisplay(order)}</div>
              <div class="info-text"><span class="info-label">ติดต่อ:</span> ${order.guest_phone || order.profiles?.email || '-'}</div>
              <div class="info-text"><span class="info-label">วันที่:</span> ${formatDate(order.created_at)}</div>
            </div>
          </div>

          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th style="text-align: left; width: 5%;">#</th>
                  <th style="text-align: left;">รายการสินค้า</th>
                  <th style="text-align: center; width: 12%;">จำนวน</th>
                  <th style="text-align: right; width: 22%;">ราคาต่อหน่วย</th>
                  <th style="text-align: right; width: 22%;">จำนวนเงิน</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="text-align: left; vertical-align: top;">1.</td>
                  <td style="text-align: left; vertical-align: top;">
                    ${order.package_name || order.credits_amount.toLocaleString() + ' เครดิต'}
                  </td>
                  <td style="text-align: center; vertical-align: top;">1</td>
                  <td style="text-align: right; vertical-align: top;">${formattedTotal} บาท</td>
                  <td style="text-align: right; vertical-align: top;">${formattedTotal} บาท</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="summary-grid">
            <div>
              <div class="notes-box">
                <div class="notes-title">หมายเหตุ</div>
                <div class="notes-content">
                  ${order.admin_notes || 'ชำระเงินเรียบร้อยแล้ว'}
                </div>
              </div>
              <div class="signature-box">
                <div class="signature-line">ผู้มีอำนาจลงนาม</div>
              </div>
            </div>
            <div>
              <div class="summary-row">
                <span>ทั้งหมด</span>
                <span>${formattedTotal} บาท</span>
              </div>
              <div class="summary-row">
                <span>ส่วนลด</span>
                <span>0.00 บาท</span>
              </div>
              <div class="summary-row">
                <span>ค่าจัดส่ง</span>
                <span>0.00 บาท</span>
              </div>
              <div class="summary-row highlight">
                <span>รวมราคาสุทธิ (ราคารวมภาษีมูลค่าเพิ่ม)</span>
                <span>${formattedTotal} บาท</span>
              </div>
              <div class="summary-row">
                <span class="info-label">ภาษีมูลค่าเพิ่ม 7%</span>
                <span>${formattedVat} บาท</span>
              </div>
              <div class="summary-row">
                <span class="info-label">ราคาไม่รวมภาษีมูลค่าเพิ่ม</span>
                <span>${formattedPreTax} บาท</span>
              </div>
            </div>
          </div>

          <div class="footer">
            ขอบพระคุณที่ใช้บริการ (Thank you)
          </div>
        </div>
      `;
      htmlPages.push(pageHtml);
    }

    const batchHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>พิมพ์ใบกำกับภาษีแบบกลุ่ม (${filteredOrders.length} รายการ)</title>
        <link href="https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700&display=swap" rel="stylesheet">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Sarabun', 'Segoe UI', sans-serif;
            color: #111;
            padding: 40px;
            font-size: 14px;
            line-height: 1.5;
          }
          .invoice-page {
            max-width: 800px;
            margin: 0 auto;
            page-break-after: always;
            padding-bottom: 20px;
          }
          .invoice-page:last-child {
            page-break-after: avoid;
          }
          .header {
            text-align: center;
            margin-bottom: 25px;
          }
          .title {
            font-size: 20px;
            font-weight: 700;
            margin-bottom: 5px;
          }
          .invoice-numbers {
            font-size: 14px;
            color: #333;
          }
          .info-grid {
            display: grid;
            grid-template-columns: 1.2fr 1fr;
            gap: 40px;
            margin-top: 30px;
            margin-bottom: 30px;
          }
          .info-title {
            font-size: 15px;
            font-weight: 700;
            margin-bottom: 8px;
            border-bottom: 1px solid #111;
            padding-bottom: 4px;
          }
          .info-text {
            font-size: 13.5px;
            margin-bottom: 4px;
          }
          .info-label {
            color: #555;
          }
          .table-container {
            margin-bottom: 30px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th {
            border-top: 2px solid #111;
            border-bottom: 2px solid #111;
            padding: 10px 8px;
            font-weight: 700;
            font-size: 13.5px;
          }
          td {
            padding: 12px 8px;
            font-size: 13.5px;
            border-bottom: 1px solid #eee;
          }
          .summary-grid {
            display: grid;
            grid-template-columns: 1.2fr 1fr;
            gap: 40px;
            margin-top: 10px;
          }
          .notes-box {
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            padding: 12px;
            min-height: 90px;
          }
          .notes-title {
            font-weight: 700;
            font-size: 12.5px;
            color: #495057;
            margin-bottom: 4px;
          }
          .notes-content {
            font-size: 12.5px;
            color: #6c757d;
          }
          .signature-box {
            border: 1px solid #dee2e6;
            border-radius: 6px;
            padding: 12px;
            height: 90px;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            align-items: center;
            background: #fff;
            margin-top: 15px;
          }
          .signature-line {
            font-size: 12.5px;
            color: #495057;
            border-top: 1px dotted #adb5bd;
            width: 80%;
            text-align: center;
            padding-top: 5px;
          }
          .summary-row {
            display: flex;
            justify-content: space-between;
            padding: 3px 0;
            font-size: 13.5px;
          }
          .summary-row.highlight {
            background: #f1f3f5;
            padding: 8px 12px;
            font-weight: 700;
            border-radius: 4px;
            font-size: 14px;
            margin: 6px 0;
          }
          .footer {
            text-align: center;
            margin-top: 60px;
            font-size: 13px;
            font-weight: 500;
          }
          @media print {
            body { padding: 10px; }
          }
        </style>
      </head>
      <body>
        ${htmlPages.join('\n')}
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(batchHtml);
      printWindow.document.close();
    }
  };

  // Approve order - mark as completed and add credits
  const handleApproveOrder = async (order: CreditOrder) => {
    if (!order.user_id) {
      toast({
        title: "ไม่สามารถอนุมัติได้",
        description: "ไม่พบ User ID สำหรับ order นี้",
        variant: "destructive",
      });
      return;
    }

    setProcessingOrderId(order.id);
    try {
      // 1. Update order status to completed
      const { error: orderError } = await (supabase as any)
        .from("credit_orders")
        .update({
          status: "completed",
          completed_at: new Date().toISOString(),
        })
        .eq("id", order.id);

      if (orderError) throw orderError;

      // 2. Add credits to user profile
      const totalCredits = order.credits_amount + order.bonus_credits;
      const { error: creditError } = await supabase.rpc("admin_add_credits" as any, {
        p_user_id: order.user_id,
        p_amount: totalCredits,
      });

      if (creditError) throw creditError;

      toast({
        title: "อนุมัติสำเร็จ",
        description: `เพิ่ม ${totalCredits.toLocaleString()} เครดิตให้ผู้ใช้แล้ว`,
      });

      // Refresh orders
      fetchOrders();
    } catch (error: any) {
      console.error("Error approving order:", error);
      toast({
        title: "เกิดข้อผิดพลาด",
        description: error.message || "ไม่สามารถอนุมัติ order ได้",
        variant: "destructive",
      });
    } finally {
      setProcessingOrderId(null);
    }
  };

  // Cancel order
  const handleCancelOrder = async (order: CreditOrder) => {
    setProcessingOrderId(order.id);
    try {
      const { error } = await (supabase as any)
        .from("credit_orders")
        .update({
          status: "cancelled",
        })
        .eq("id", order.id);

      if (error) throw error;

      toast({
        title: "ยกเลิกสำเร็จ",
        description: "ยกเลิก order เรียบร้อยแล้ว",
      });

      // Refresh orders
      fetchOrders();
    } catch (error: any) {
      console.error("Error cancelling order:", error);
      toast({
        title: "เกิดข้อผิดพลาด",
        description: error.message || "ไม่สามารถยกเลิก order ได้",
        variant: "destructive",
      });
    } finally {
      setProcessingOrderId(null);
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return order.price_thb >= 100; // Show only >= 100 baht
    if (filter === 'free') return order.price_thb === 0 && order.status === 'completed'; // Free completed only
    if (filter === 'pending') return order.status === 'pending' || order.status === 'processing';
    if (filter === 'completed') return order.status === 'completed';
    if (filter === 'failed') return order.status === 'failed' || order.status === 'cancelled' || order.status === 'expired';
    return true;
  });

  const stats = {
    total: orders.length,
    completed: orders.filter(o => o.status === 'completed').length,
    pending: orders.filter(o => o.status === 'pending' || o.status === 'processing').length,
    totalRevenue: orders.filter(o => o.status === 'completed').reduce((sum, o) => sum + o.price_thb, 0),
  };

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  // Calculate daily chart data (last 14 days)
  const getDailyChartData = () => {
    const last14Days: Record<string, { credits: number; revenue: number }> = {};
    const today = new Date();

    for (let i = 13; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateKey = date.toISOString().split('T')[0];
      last14Days[dateKey] = { credits: 0, revenue: 0 };
    }

    orders
      .filter(o => o.status === 'completed')
      .forEach(order => {
        const dateKey = order.created_at.split('T')[0];
        if (last14Days[dateKey]) {
          last14Days[dateKey].credits += order.credits_amount + order.bonus_credits;
          last14Days[dateKey].revenue += order.price_thb;
        }
      });

    return Object.entries(last14Days).map(([date, data]) => ({
      date: new Date(date).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' }),
      credits: data.credits,
      revenue: data.revenue
    }));
  };

  const chartData = getDailyChartData();
  const maxCredits = Math.max(...chartData.map(d => d.credits), 1);

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card variant="glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Receipt className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">รายการทั้งหมด</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card variant="glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.completed}</p>
                <p className="text-xs text-muted-foreground">สำเร็จ</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card variant="glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pending}</p>
                <p className="text-xs text-muted-foreground">รอดำเนินการ</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card variant="glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">฿{stats.totalRevenue.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">รายได้รวม</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Orders List */}
      <Card variant="glass">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            รายการเติมเครดิต
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowChart(!showChart)}
              className="gap-2"
            >
              <BarChart3 className="h-4 w-4" />
              {showChart ? 'ซ่อนกราฟ' : 'แสดงกราฟ'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrintBatchTaxInvoices}
              className="gap-2 border-blue-500/50 text-blue-600 hover:bg-blue-500/10"
            >
              <FileText className="h-4 w-4" />
              พิมพ์ใบกำกับภาษีตามตัวกรอง ({filteredOrders.length})
            </Button>
            <div className="flex gap-1 flex-wrap">
              {(['all', 'free', 'pending', 'completed', 'failed'] as const).map((f) => (
                <Button
                  key={f}
                  variant={filter === f ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilter(f)}
                  className={f === 'free' ? 'border-green-500/50 text-green-600 hover:bg-green-500/10' : ''}
                >
                  {f === 'all' ? 'ทั้งหมด (≥100฿)' : f === 'free' ? '🎁 ฟรี' : f === 'pending' ? 'รอดำเนินการ' : f === 'completed' ? 'สำเร็จ' : 'ล้มเหลว'}
                </Button>
              ))}
            </div>
            <Button variant="outline" size="icon" onClick={fetchOrders} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Daily Chart */}
          {showChart && (
            <div className="mb-6 p-4 rounded-xl bg-muted/30 border border-border/50">
              <h4 className="text-sm font-medium mb-4">เครดิตที่เติมรายวัน (14 วันล่าสุด)</h4>
              <div className="flex items-end gap-1 h-32">
                {chartData.map((day, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div
                      className="w-full bg-primary/80 rounded-t transition-all hover:bg-primary"
                      style={{ height: `${(day.credits / maxCredits) * 100}%`, minHeight: day.credits > 0 ? '4px' : '0' }}
                      title={`${day.credits.toLocaleString()} เครดิต / ฿${day.revenue.toLocaleString()}`}
                    />
                    <span className="text-[10px] text-muted-foreground rotate-[-45deg] origin-left whitespace-nowrap">
                      {day.date}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-border/50 grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-primary">
                    {chartData.reduce((sum, d) => sum + d.credits, 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">เครดิตรวม 14 วัน</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-500">
                    ฿{chartData.reduce((sum, d) => sum + d.revenue, 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">รายได้รวม 14 วัน</p>
                </div>
              </div>
            </div>
          )}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">ไม่มีรายการ</p>
            </div>
          ) : (
            <div className="space-y-3">
              {paginatedOrders.map((order) => (
                <div
                  key={order.id}
                  className="flex items-center justify-between p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors border border-border/50"
                >
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => order.user_id && navigate(`/admin?tab=history&userId=${order.user_id}`)}
                      disabled={!order.user_id}
                      className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                      title={order.user_id ? "ดูประวัติการใช้งาน" : "ไม่มี User ID"}
                    >
                      <User className="h-5 w-5 text-primary" />
                    </button>
                    <div>
                      <p className="font-semibold">{getUserDisplay(order)}</p>
                      <p className="text-xs text-muted-foreground">{getBuyerEmail(order)}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{order.package_name || `${order.credits_amount.toLocaleString()} เครดิต`}</span>
                        <span>•</span>
                        <span>{getPaymentMethodLabel(order.payment_method)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <Calendar className="h-3 w-3" />
                        <span>{formatDate(order.created_at)}</span>
                        {order.bonus_credits > 0 && (
                          <span className="text-green-500">+{order.bonus_credits} โบนัส</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-lg font-bold">฿{order.price_thb.toLocaleString()}</p>
                      {getStatusBadge(order.status)}
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleViewBuyerDetails(order)}
                      title="รายละเอียดผู้ซื้อ"
                    >
                      <User className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handlePrintReceipt(order)}
                      title="ปริ้นใบเสร็จ"
                      className={printedOrders.has(order.id) ? 'bg-green-500/20 border-green-500/50 text-green-600 hover:bg-green-500/30' : ''}
                    >
                      <Printer className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handlePrintTaxInvoice(order)}
                      title="พิมพ์ใบกำกับภาษีอย่างย่อ"
                      className="hover:bg-blue-500/10 hover:text-blue-500"
                    >
                      <FileText className="h-4 w-4" />
                    </Button>
                    {(order.status === 'pending' || order.status === 'processing') && (
                      <>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleApproveOrder(order)}
                          disabled={processingOrderId === order.id}
                          title="อนุมัติ"
                          className="bg-green-500/10 border-green-500/50 text-green-600 hover:bg-green-500/20"
                        >
                          {processingOrderId === order.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <CheckCircle2 className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleCancelOrder(order)}
                          disabled={processingOrderId === order.id}
                          title="ยกเลิก"
                          className="bg-red-500/10 border-red-500/50 text-red-600 hover:bg-red-500/20"
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    {order.payment_slip_url && (
                      <Button variant="outline" size="icon" asChild>
                        <a href={order.payment_slip_url} target="_blank" rel="noopener noreferrer">
                          <Eye className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-border/50">
                  <p className="text-sm text-muted-foreground">
                    แสดง {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredOrders.length)} จาก {filteredOrders.length} รายการ
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium px-2">
                      {currentPage} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showBuyerDialog} onOpenChange={setShowBuyerDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>รายละเอียดผู้ซื้อ</DialogTitle>
            <DialogDescription>
              ข้อมูลจากคำสั่งซื้อ {selectedOrder?.id}
            </DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">ผู้ซื้อ</span>
                <span className="col-span-2 font-medium">
                  {getUserDisplay(selectedOrder)}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">อีเมล</span>
                <span className="col-span-2">{getBuyerEmail(selectedOrder)}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">เบอร์โทร</span>
                <span className="col-span-2">{getBuyerPhone(selectedOrder)}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">User ID</span>
                <code className="col-span-2 text-xs bg-muted px-2 py-1 rounded">
                  {selectedOrder.user_id || "Guest"}
                </code>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">แพ็กเกจ</span>
                <span className="col-span-2">
                  {selectedOrder.package_name || `${selectedOrder.credits_amount.toLocaleString()} เครดิต`}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">ราคา</span>
                <span className="col-span-2">฿{selectedOrder.price_thb.toLocaleString()}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">สถานะ</span>
                <span className="col-span-2">{getStatusBadge(selectedOrder.status)}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">ช่องทางชำระเงิน</span>
                <span className="col-span-2">{getPaymentMethodLabel(selectedOrder.payment_method)}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">วันที่สั่งซื้อ</span>
                <span className="col-span-2">{formatDate(selectedOrder.created_at)}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderManagement;
